import { useState, useContext, useRef } from 'react';
import React from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import './cooperativeForm.scss';
import {
  addNewAdmin,
  addStructure,
  removeAdmin,
  updateStructure,
} from 'services/API/Administration';
import {
  inputIntegerValidator,
  inputNumberMinMaxValidator,
  inputNumberValidator,
  inputRequired,
  inputVerifyMail,
} from 'components/generics/Validators/validator';
import { validateFormData } from 'components/generics/Validators/form.validate';
import { LoadingContext } from 'utils/context';
import {
  checkTypeStringRegexDatagrid,
  displayErrorMessage,
  displayMessage,
} from 'utils/tools_functions';
import { customDialogAddEditCommonTheme } from 'assets/styles/themes/Dialog/generic_dialog_add_edit_parcel';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  Input,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ThemeProvider,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { Add, Close } from '@mui/icons-material';
import { customButtonTheme } from 'assets/styles/themes/generic_button_mui';

const DEFAULT_FORM = {
  name: '',
  hasRights: true,
  isActive: true,
  passwordMaxValidity: '',
  passwordMinLength: '',
  twoFactorEnabled: false,
  mfaIntervalDays: 1,
};
const DEFAULT_ADMIN = {
  firstname: '',
  lastname: '',
  email: '',
  roles: ['ROLE_ADMIN'],
  cooperative: 0,
};

const AddEditStructureModal = ({
  modal,
  setModal,
  onSubmitSuccess,
  isEdit,
  edit,
  getStructures,
  setEditData,
}) => {
  const { t } = useTranslation();
  const [data, setData] = useState(
    isEdit
      ? { ...DEFAULT_FORM, ...edit, mfaIntervalDays: edit.mfaIntervalDays ?? 1 }
      : DEFAULT_FORM,
  );
  const [isActive, setIsActive] = useState(
    isEdit ? (edit.status === 'active' ? true : false) : true,
  );
  const [currentAdmins, setCurrentAdmins] = useState(
    isEdit
      ? edit.users.filter(
          (user) => user.roles?.includes('ROLE_ADMIN') || user.roles?.includes('ROLE_SUPER_ADMIN'),
        )
      : [],
  );
  const [avatar, setAvatar] = useState(null);
  const [newAdmin, setNewAdmin] = useState([]);
  const [addAdmin, setAddAdmin] = useState(DEFAULT_ADMIN);
  const [removeId, setRemoveId] = useState([]);
  const [loading, setLoading] = useState(false);
  const updateNewAdmin = (key, value) => setAddAdmin({ ...addAdmin, [key]: value });
  const updateForm = (key, value) => setData({ ...data, [key]: value });
  const [uploadedFile, setUploadedFile] = useState(null);
  const [errorsFormDefault, setErrorsFormDefault] = useState({
    formError: false,
    name: {
      message: '',
      validator: [inputRequired],
    },
    passwordMaxValidity: {
      message: '',
      validator: [inputRequired, inputIntegerValidator, inputNumberMinMaxValidator(7, 730)],
    },
    passwordMinLength: {
      message: '',
      validator: [inputRequired, inputIntegerValidator, inputNumberMinMaxValidator(12, 64)],
    },
    mfaIntervalDays: {
      message: '',
      validator: [inputIntegerValidator, inputNumberMinMaxValidator(1, 365)],
    },
  });
  const [errorsFormAdmin, setErrorsFormAdmin] = useState({
    formError: false,
    firstname: {
      message: '',
      validator: [inputRequired],
    },
    lastname: {
      message: '',
      validator: [inputRequired],
    },
    email: {
      message: '',
      validator: [inputRequired, inputVerifyMail],
    },
  });
  const setTextLoader = useContext(LoadingContext);

  const status = [
    { active: t('common.status.active'), status: t('common.status.active') },
    { active: t('common.status.inactive'), status: t('common.status.inactive') },
  ];
  const ref = useRef();

  const handleChosenImage = (e) => {
    const file = e.target.files[0];
    if (file?.size < 50000) {
      setUploadedFile(file);
      setAvatar(URL.createObjectURL(file));
    } else {
      toast.warning(t('errors.imageTooLarge'));
    }
  };

  const checkDisabledAddAdminButton =
    addAdmin && Object.keys(addAdmin).length
      ? !addAdmin.firstname || !addAdmin.lastname || !addAdmin.email
        ? true
        : false
      : true;

  const handleSetActive = (e) => {
    if (e === t('common.status.active')) {
      setIsActive(true);
    } else {
      setIsActive(false);
    }
  };

  const addAdminHandle = () => {
    if (addAdmin !== DEFAULT_ADMIN) {
      errorsFormAdmin.formError = false;
      setErrorsFormAdmin({ ...validateFormData(addAdmin, errorsFormAdmin) });
      if (!errorsFormAdmin.formError) {
        const newArr = newAdmin;
        newArr.push(addAdmin);
        setNewAdmin(newArr);
        setAddAdmin(DEFAULT_ADMIN);
      }
    }
  };

  const deleteCurrentAdmin = (admin) => {
    const newDeleteArray = removeId;
    newDeleteArray.push(admin.id);
    setRemoveId(newDeleteArray);
    const newCurrentAdmins = currentAdmins.filter((currentAdmin) => currentAdmin.id !== admin.id);
    setCurrentAdmins(newCurrentAdmins);
  };

  const deleteNewAdmin = (index) => {
    const arr = [];
    for (let i = 0; i < newAdmin.length; i++) {
      if (i !== index) {
        arr.push(newAdmin[i]);
      }
    }
    setNewAdmin(arr);
  };

  const triggerShortcut = (e) => {
    if (e.keyCode === 13) {
      handleOnSubmit();
    }
  };

  const handleOnSubmit = () => {
    errorsFormDefault.formError = false;
    setErrorsFormDefault({ ...validateFormData(data, errorsFormDefault) });
    if (!errorsFormDefault.formError) {
      setLoading(true);
      setTextLoader.customText = displayMessage('UPDATE_STRUCTURE_LOADER');
      if (isEdit) {
        let formData = new FormData();
        if (uploadedFile) {
          formData.append('logo', uploadedFile);
        }
        formData.append('name', data.name);
        formData.append('hasRights', isActive);
        formData.append('isActive', isActive);
        formData.append('passwordMaxValidity', data.passwordMaxValidity);
        formData.append('passwordMinLength', data.passwordMinLength);
        formData.append('twoFactorEnabled', +data.twoFactorEnabled);
        formData.append('mfaIntervalDays', +data.mfaIntervalDays);
        removeId.forEach((id) => {
          removeAdmin(id)
            .then(() => {
              toast.success(t('success.adminDeleted'));
              getStructures();
            })
            .catch((err) => {
              toast.error(err.message);
            });
        });
        newAdmin.forEach((admin) => {
          admin.cooperative = data.id;
          addNewAdmin(admin)
            .then(() => {
              toast.success(
                t('success.adminCreated', { firstname: admin.firstname, lastname: admin.lastname }),
              );
              getStructures();
            })
            .catch((err) => {
              if (err.response.data.status === 409) {
                return toast.error(displayErrorMessage('ERR_ADD_EXISTING_EMAIL', admin));
              }
              toast.error(err.response.data.message);
            });
        });
        updateStructure(formData, data.id)
          .then(() => {
            toast.success(t('success.structureUpdated', { name: data.name }));
            getStructures();
          })
          .catch((err) => {
            toast.error(err.message);
          });
        onSubmitSuccess();
      } else {
        let formData = new FormData();
        formData.append('logo', uploadedFile);
        formData.append('name', data.name);
        // HasRights have the same value than isActive for now
        formData.append('hasRights', isActive);
        formData.append('isActive', isActive);
        formData.append('passwordMaxValidity', data.passwordMaxValidity);
        formData.append('passwordMinLength', +data.passwordMinLength);
        formData.append('twoFactorEnabled', +data.twoFactorEnabled);
        formData.append('doubleAuthentification', +data.mfaIntervalDays);
        formData.append(
          'doubleAuthentificationMaxValidity',
          data.doubleAuthentificationMaxValidity,
        );
        setTextLoader.customText = displayMessage('CREATE_STRUCTURE_LOADER');
        addStructure(formData)
          .then((response) => {
            toast.success(t('success.structureCreated', { name: data.name }));
            const structureId = response.data.data.id;
            newAdmin.forEach((admin) => {
              admin.cooperative = structureId;
              addNewAdmin(admin)
                .then(() => {
                  toast.success(
                    t('success.adminCreated', {
                      firstname: admin.firstname,
                      lastname: admin.lastname,
                    }),
                  );
                  getStructures();
                })
                .catch((err) => {
                  if (err.response.data.status === 409) {
                    return toast.error(displayErrorMessage('ERR_ADD_EXISTING_EMAIL', admin));
                  }
                  toast.error(err.response.data.message);
                });
            });
            getStructures();
          })
          .catch((err) => {
            toast.error(err.message);
          });
        onSubmitSuccess();
      }
    }
    setLoading(false);
  };

  return (
    <ThemeProvider theme={customDialogAddEditCommonTheme}>
      <Dialog
        maxWidth="md"
        fullWidth
        open={modal}
        ref={ref}
        PaperProps={{ ref: ref }}
        onClose={() => {
          (setModal(false), setEditData(null));
        }}
        onKeyDown={(e) => triggerShortcut(e)}
        id="custom-add-dialog"
      >
        <div className="add_culture_container">
          <Box className="modal_header">
            <DialogTitle>
              {!isEdit ? t('title.addStructure') : t('title.editStructure')}
            </DialogTitle>
            <ClearIcon className="close-icon" onClick={() => setModal(false)} />
          </Box>
          <DialogContent className="modal-container">
            <div className="add-header-container">
              <Box>
                <InputLabel id="nameLabel" htmlFor="name">
                  {t('common.name')}
                  <span className="label_asterisk">*</span>
                </InputLabel>
                <TextField
                  id="name"
                  onChange={(e) => {
                    updateForm('name', checkTypeStringRegexDatagrid(e.target.value));
                  }}
                  value={data.name}
                  placeholder={t('common.name')}
                  disabled={loading}
                  className={errorsFormDefault?.name?.message ? 'MuiErrorForm' : ''}
                  error={errorsFormDefault?.name?.message ? true : false}
                  helperText={errorsFormDefault?.name?.message}
                />
              </Box>
              <Box>
                <InputLabel id="statusLabel" htmlFor="status">
                  {t('form.status')}
                </InputLabel>
                <Select
                  id="status"
                  onChange={(e) => handleSetActive(e.target.value)}
                  name="status"
                  disabled={loading}
                  value={
                    isActive === true ? t('common.status.active') : t('common.status.inactive')
                  }
                >
                  {status.map((item) => (
                    <MenuItem key={item.active} value={item.active}>
                      {item.active === t('common.status.active')
                        ? t('common.status.active')
                        : t('common.status.inactive')}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </div>
            <fieldset className="add_culture_fieldset">
              <legend className="inputLabel-admin">
                {t('title.addAdmin')}
                <ThemeProvider theme={customButtonTheme}>
                  <Button
                    onClick={() => addAdminHandle()}
                    type="button"
                    id="add_coProduct_button"
                    disabled={checkDisabledAddAdminButton}
                  >
                    {t('common.add')} <Add id="MuiSvgAddIcon" />
                  </Button>
                </ThemeProvider>
              </legend>
              <Box className="add-admin-list">
                <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
                  {currentAdmins.length > 0 &&
                    currentAdmins.map((admin) => (
                      <div key={admin.id}>
                        <Button
                          variant="contained"
                          onClick={() => deleteCurrentAdmin(admin)}
                          className="admin-button"
                          disabled={loading}
                          endIcon={<Close width={13} height={13} />}
                        >
                          {`${admin.firstname} ${admin.lastname}`}
                        </Button>
                      </div>
                    ))}
                  {newAdmin.map((admin, index) => (
                    <div key={index}>
                      <Button
                        variant="contained"
                        onClick={() => deleteNewAdmin(index)}
                        className="admin-button"
                        disabled={loading}
                        endIcon={<Close width={13} height={13} />}
                      >
                        {`${admin.firstname} ${admin.lastname}`}
                      </Button>
                    </div>
                  ))}
                </Box>
              </Box>
              <React.Fragment>
                <Box>
                  <InputLabel name="firstnameLabel" htmlFor="firstname">
                    {t('form.firstname')}
                  </InputLabel>
                  <TextField
                    onChange={(e) => {
                      updateNewAdmin('firstname', checkTypeStringRegexDatagrid(e.target.value));
                    }}
                    value={addAdmin.firstname}
                    id="firstname"
                    placeholder={t('form.firstname')}
                    disabled={loading}
                    className={errorsFormAdmin?.firstname?.message ? 'MuiErrorForm' : ''}
                    error={errorsFormAdmin?.firstname?.message ? true : false}
                    helperText={errorsFormAdmin?.firstname?.message}
                  />
                </Box>
                <Box>
                  <InputLabel name="lastnameLabel" htmlFor="lastname">
                    {t('form.lastname')}
                  </InputLabel>
                  <TextField
                    onChange={(e) => {
                      updateNewAdmin('lastname', checkTypeStringRegexDatagrid(e.target.value));
                    }}
                    value={addAdmin.lastname}
                    id="lastname"
                    placeholder={t('form.lastname')}
                    disabled={loading}
                    className={errorsFormAdmin?.name?.message ? 'MuiErrorForm' : ''}
                    error={errorsFormAdmin?.lastname?.message ? true : false}
                    helperText={errorsFormAdmin?.lastname?.message}
                  />
                </Box>
                <Box>
                  <InputLabel name="emailLabel" htmlFor="email">
                    {t('form.email')}
                  </InputLabel>
                  <TextField
                    onChange={(e) => {
                      updateNewAdmin('email', e.target.value);
                    }}
                    value={addAdmin.email}
                    id="email"
                    placeholder={t('form.email')}
                    type="email"
                    disabled={loading}
                    className={errorsFormAdmin?.name?.message ? 'MuiErrorForm' : ''}
                    error={errorsFormAdmin?.email?.message ? true : false}
                    helperText={errorsFormAdmin?.email?.message}
                  />
                </Box>
              </React.Fragment>
            </fieldset>
            <fieldset className="add_culture_fieldset">
              <legend className="inputLabel-admin">
                {t('label.logo')}
                <InputLabel htmlFor="file" className="label-input-file">
                  <Input
                    id="file"
                    type="file"
                    className="input-file"
                    onChange={handleChosenImage}
                  />
                  <ThemeProvider theme={customButtonTheme}>
                    <Button
                      variant="contained"
                      component="span"
                      type="button"
                      id="add_coProduct_button"
                    >
                      {t('button.browse')}
                    </Button>
                  </ThemeProvider>
                </InputLabel>
              </legend>
              <Box className="add-logo-structure">
                <Box className="container-logo">
                  {avatar ? (
                    <Box>
                      <img src={avatar} alt="logo"></img>
                    </Box>
                  ) : data?.logo ? (
                    <img src={data?.logo} alt="logo"></img>
                  ) : (
                    <span>{t('label.noLogoToDisplay')}</span>
                  )}
                </Box>
              </Box>
            </fieldset>
            <fieldset className="add_culture_fieldset">
              <legend className="inputLabel-admin">{t('form.passwordParameters')}</legend>
              <Box>
                <InputLabel htmlFor="passwordMaxValidity">
                  {t('form.passwordValidity')}
                  <span className="label_asterisk">*</span>
                </InputLabel>
                <TextField
                  onChange={(e) => {
                    updateForm('passwordMaxValidity', e.target.value);
                  }}
                  value={data.passwordMaxValidity}
                  id="passwordMaxValidity"
                  placeholder="min : 7, max : 730"
                  disabled={loading}
                  className={errorsFormDefault?.passwordMaxValidity?.message ? 'MuiErrorForm' : ''}
                  error={!!errorsFormDefault?.passwordMaxValidity?.message}
                  helperText={errorsFormDefault?.passwordMaxValidity?.message}
                />
              </Box>
              <Box>
                <InputLabel htmlFor="passwordMinLength">
                  {t('form.passwordMinCaracter')}
                  <span className="label_asterisk">*</span>
                </InputLabel>
                <TextField
                  onChange={(e) => {
                    updateForm('passwordMinLength', e.target.value);
                  }}
                  value={data.passwordMinLength}
                  id="passwordMinLength"
                  placeholder="min : 12, max : 64"
                  disabled={loading}
                  className={errorsFormDefault?.passwordMinLength?.message ? 'MuiErrorForm' : ''}
                  error={!!errorsFormDefault?.passwordMinLength?.message}
                  helperText={errorsFormDefault?.passwordMinLength?.message}
                />
              </Box>
            </fieldset>
            <fieldset className="add_culture_fieldset">
              <legend className="inputLabel-admin">{t('form.doubleAuthentification')}</legend>
              <Box>
                <InputLabel htmlFor="twoFactorEnabled">
                  {t('form.doubleAuthentificationActivate')}
                </InputLabel>
                <Checkbox
                  checked={data?.twoFactorEnabled}
                  id="twoFactorEnabled"
                  label="twoFactorEnabled"
                  onChange={(e) => {
                    updateForm('twoFactorEnabled', e.target.checked);
                  }}
                />
              </Box>
              <Box>
                <InputLabel htmlFor="mfaIntervalDays">
                  {t('form.doubleAuthentificationDuration')}
                </InputLabel>
                <TextField
                  onChange={(e) => {
                    updateForm('mfaIntervalDays', e.target.value);
                  }}
                  value={data.mfaIntervalDays}
                  id="mfaIntervalDays"
                  placeholder="min : 1, max : 365"
                  disabled={!data.twoFactorEnabled}
                  className={errorsFormDefault?.mfaIntervalDays?.message ? 'MuiErrorForm' : ''}
                  error={!!errorsFormDefault?.mfaIntervalDays?.message}
                  helperText={errorsFormDefault?.mfaIntervalDays?.message}
                />
              </Box>
            </fieldset>
            <Box className="button-submit-structure">
              <ThemeProvider theme={customButtonTheme}>
                <Box>
                  <Button onClick={() => handleOnSubmit()} disabled={loading}>
                    {t('common.save')}
                  </Button>
                </Box>
              </ThemeProvider>
            </Box>
          </DialogContent>
        </div>
      </Dialog>
    </ThemeProvider>
  );
};

export default AddEditStructureModal;
